/**
 * アーティストページのNew Releaseをその場でページ送りする
 *
 * @description 中身はACFの繰り返しで、最初から全部が置いてある。1ページに並べる数が
 *              PCとSPで違うので送り先のURLを持てず、ニュース一覧のように
 *              サーバーへ取りに行く作りにできない。数を数えて出し入れする。
 *
 *              JSが動かないときは全部が出たままでページ送りは出ない。
 */

import { armFade, disarmFade, fadeOut, fadeIn } from './utils-swapfade';

/** 節そのものを指す印。テンプレート側が付ける */
const ROOT = '[data-releasepager]';

/** 1ページに並べる数。デザインの桁数に合わせる */
const PER_PC = 3;
const PER_SP = 4;

/** SPに切り替わる幅。_st-mediaquery.scss の mq() と同じ */
const SP = '(max-width: 1024px)';

/** 両端と現在地の周りだけ番号を出す。間は … で省く。Pager.php と同じ省き方 */
const EDGE = 1;
const AROUND = 2;

/** 外したい手続き。非同期遷移で節ごと作り直すときに使う */
let detach: (() => void) | null = null;

/**
 * 出す番号の並びを作る
 *
 * @param total 総ページ数
 * @param current 現在のページ
 * @returns 番号の並び。null は省いた場所
 */
const pagesOf = (total: number, current: number): (number | null)[] => {
    const out: (number | null)[] = [];

    for (let i = 1; i <= total; i++) {
        if (i <= EDGE || i > total - EDGE || Math.abs(i - current) <= AROUND) {
            out.push(i);
        } else if (out[out.length - 1] !== null) {
            out.push(null);
        }
    }

    return out;
};

/**
 * 前後の矢印を作る
 *
 * @description URLは変わらないので、押せるときはボタン。押せないときは
 *              Pager.php と同じく span にして読み上げからも外す
 * @param dir 向き
 * @param enabled 押せるか
 * @param go 押されたときの行き先
 * @returns 矢印
 */
const navOf = (dir: 'prev' | 'next', enabled: boolean, go: () => void): HTMLElement => {
    const el = document.createElement(enabled ? 'button' : 'span');

    el.className = `Pager__nav attr-${dir}${enabled ? '' : ' is-disabled'}`;

    if (enabled && el instanceof HTMLButtonElement) {
        el.type = 'button';
        el.setAttribute('aria-label', dir === 'prev' ? '前のページ' : '次のページ');
        el.addEventListener('click', go);
    } else {
        el.setAttribute('aria-hidden', 'true');
    }

    ['attr-1', 'attr-2'].forEach((name) => {
        const arrow = document.createElement('span');

        arrow.className = `Pager__navArrow ${name}`;
        el.appendChild(arrow);
    });

    return el;
};

/**
 * 番号の一覧を作る
 *
 * @param total 総ページ数
 * @param current 現在のページ
 * @param go 押されたときの行き先
 * @returns 番号の一覧
 */
const listOf = (total: number, current: number, go: (page: number, from: string | null) => void): HTMLElement => {
    const ul = document.createElement('ul');

    ul.className = 'Pager__list';

    pagesOf(total, current).forEach((page) => {
        const li = document.createElement('li');

        li.className = 'Pager__listItem';

        if (page === null) {
            const dots = document.createElement('span');

            dots.className = 'Pager__listItemDots';
            dots.setAttribute('aria-hidden', 'true');
            li.appendChild(dots);
        } else if (page === current) {
            const now = document.createElement('span');

            now.className = 'Pager__listItemNum is-current';
            now.setAttribute('aria-current', 'page');
            now.textContent = String(page);
            li.appendChild(now);
        } else {
            const btn = document.createElement('button');

            btn.type = 'button';
            btn.className = 'Pager__listItemNum';
            btn.textContent = String(page);
            // 押した番号は現在地のspanに変わって消えるので、行き先は持たせない
            btn.addEventListener('click', () => go(page, null));
            li.appendChild(btn);
        }

        ul.appendChild(li);
    });

    return ul;
};

/**
 * 読み上げ用の領域を用意する
 *
 * @param root 節
 * @returns 知らせを書き込む要素
 */
const statusOf = (root: Element): HTMLElement => {
    const found = root.querySelector<HTMLElement>('[data-releasepager-status]');

    if (found) {
        return found;
    }

    const el = document.createElement('p');

    el.setAttribute('data-releasepager-status', '');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.className = 'listpagerStatus';
    root.appendChild(el);

    return el;
};

/**
 * 節の見出しを画面内へ戻す
 *
 * @description 押したページ送りは一覧の下端にあるので、入れ替えたあと見出しが
 *              画面の上に外れていることが多い。外れているときだけ戻す
 * @param root 節
 * @returns {void}
 */
const bringIntoView = (root: Element): void => {
    const top = root.getBoundingClientRect().top;

    if (top >= 0) {
        return;
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: window.scrollY + top, behavior: reduced ? 'auto' : 'smooth' });
};

/**
 * 押したものが描き直しで消えるので、焦点を置き直す
 *
 * @description 同じ役目の控えが残っていればそこへ、端に着いて押せなくなったときは
 *              ページ送りそのものへ寄せる。置き直さないと焦点がbodyへ落ちて、
 *              鍵盤の人はページの先頭から辿り直しになる
 * @param pager ページ送りの入れ物
 * @param want 押したものを指す目印。番号のように行き先が消えるものはnull
 * @returns {void}
 */
const refocus = (pager: HTMLElement, want: string | null): void => {
    const again = want ? pager.querySelector<HTMLElement>(want) : null;

    if (again) {
        again.focus();

        return;
    }

    const nav = pager.querySelector<HTMLElement>('.Pager');

    if (!nav) {
        return;
    }

    nav.tabIndex = -1;
    nav.focus();
};

/**
 * その場のページ送りを止める
 *
 * @returns {void}
 */
const destroyReleasePager = (): void => {
    detach?.();
    detach = null;
};

/**
 * その場のページ送りを始める
 *
 * @returns {void}
 */
const initReleasePager = (): void => {
    destroyReleasePager();

    const root = document.querySelector<HTMLElement>(ROOT);
    const list = root?.querySelector<HTMLElement>('[class$="releaseList"]');
    const pager = root?.querySelector<HTMLElement>('[class$="releasePager"]');

    if (!root || !list || !pager) {
        return;
    }

    const items = Array.from(list.children) as HTMLElement[];

    if (!items.length) {
        return;
    }

    const sp = window.matchMedia(SP);
    let page = 1;

    // 伏せているあいだに次を押されると、戻す前にまた伏せて中身が飛ぶ
    let busy = false;

    armFade(list);

    /**
     * 今の画面幅で1ページに並べる数を返す
     *
     * @returns 並べる数
     */
    const per = (): number => (sp.matches ? PER_SP : PER_PC);

    /**
     * 今のページに合わせて札とページ送りを描き直す
     *
     * @param announce 読み上げに知らせるか。画面幅の変化では知らせない
     * @returns {void}
     */
    const draw = (announce: boolean): void => {
        const size = per();
        const total = Math.max(1, Math.ceil(items.length / size));

        page = Math.min(Math.max(1, page), total);

        items.forEach((el, i) => {
            el.hidden = Math.floor(i / size) !== page - 1;
        });

        if (total < 2) {
            // 空にしておくと :empty で枠ごと消えるので、余白だけ残らない
            pager.replaceChildren();

            return;
        }

        /**
         * 指定のページへ移る
         *
         * @param next 行き先
         * @returns {void}
         */
        const go = async (next: number, from: string | null): Promise<void> => {
            if (busy) {
                return;
            }

            busy = true;

            await fadeOut(list);

            page = next;
            draw(true);
            bringIntoView(root);
            refocus(pager, from);
            fadeIn(list);

            busy = false;
        };

        const nav = document.createElement('nav');

        nav.className = 'Pager';
        nav.setAttribute('aria-label', 'ページ送り');
        nav.appendChild(navOf('prev', page > 1, () => go(page - 1, 'button.Pager__nav.attr-prev')));
        nav.appendChild(listOf(total, page, go));
        nav.appendChild(navOf('next', page < total, () => go(page + 1, 'button.Pager__nav.attr-next')));

        pager.replaceChildren(nav);

        if (announce) {
            statusOf(root).textContent = `${page}ページ目を表示しました`;
        }
    };

    /**
     * 画面幅が変わったら並べる数が変わるので引き直す
     *
     * @returns {void}
     */
    const onChange = (): void => draw(false);

    draw(false);
    sp.addEventListener('change', onChange);

    detach = () => {
        sp.removeEventListener('change', onChange);
        disarmFade(list);
        items.forEach((el) => {
            el.hidden = false;
        });
    };
};

export { initReleasePager, destroyReleasePager };

document.addEventListener('DOMContentLoaded', initReleasePager);
